import { Box, Slider, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-admin';
import { useColorScheme } from '../../hooks/use-color-scheme';
import { useDataType } from '../../hooks/use-data-type';
import { useColor, useColorGradient } from '../../hooks/use-color';


export const ColorValueRangeSlider = ({ min, max, disabled }: { min: number, max: number, disabled: boolean }) => {
  const [value, setValue] = useStore<number[]>('map.valueRange', [min, max]);
  const colorScheme = useColorScheme();
  const [colorGradient] =  useColorGradient();
  const [_, getDataTypeString] = useDataType();
  const [perc, setPerc] = useState([min, max]);

  const [, colorMin, colorMax, colorReverse] = useColor();

  useEffect(() => {
    setValue([min, max]);
  }, [min, max]);

  const handleChange = useCallback((_: Event, value: number | number[]) => {
    const newValue = value as number[];
    setValue(newValue);
    setPerc(newValue.map((val) => ((val - min) * 10.0 / ((max - min) * 10.0)) * 100.0));
  }, [min, max]);

  const hasRange = useMemo(() => max > min, [min, max]);

  const step = useMemo(() => (max - min) / 100.0, [min, max]);

  const marks = useMemo(() => [
    {
      value: min,
      label: getDataTypeString(min),
    },
    {
      value: max,
      label: getDataTypeString(max)
    },
  ], [min, max]);

  const railColor = useMemo(() => {
    return `linear-gradient(to right, ${colorMin} 0% ${perc[0]}%, ${colorMax} ${perc[0]}% ${perc[1]}%, ${colorMax} ${perc[1]}% 100%)`;
  }, [colorMin, colorMax, perc])

  return (
    <Box sx={{ width: 200, marginTop: 0 }}>
      <Typography id='range-slider' variant='caption' gutterBottom>
        Value Range
      </Typography>
      <Slider
        aria-labelledby="range-slider"
        disabled={!hasRange || disabled}
        getAriaValueText={getDataTypeString}
        valueLabelFormat={getDataTypeString}
        min={min}
        max={max}
        step={step}
        marks={hasRange ? marks : false}
        onChange={handleChange}
        sx={{
          '& .MuiSlider-thumb': {
            borderRadius: '1px',
            border: '1px solid #ccc',
          },
          '& .MuiSlider-thumb[data-index="0"]': {
            color: colorScheme ? colorMin : 'grey',
          },
          '& .MuiSlider-thumb[data-index="1"]': {
            color: colorScheme ? colorMax : 'grey',
          },
          '& .MuiSlider-rail': {
            height: 5,
            background: railColor,
            border: 1,
            opacity: 1,
            borderRadius: '1px',
          },
          '& .MuiSlider-track': {
            borderRadius: '1px',
            height: 10,
            background: colorGradient,
            transformOrigin: '50% 25%',
            backgroundSize: 'contain',
            border: 0,
            opacity: 1
          },
          width: '100%'
        }}
        value={value}
        valueLabelDisplay="auto"
      />
    </Box>
  );
};
